import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { ScrollArea, ScrollBar } from '@/components/ui/scroll-area';
import { cn } from '@/lib/utils';
import { format } from 'date-fns';
import { Calendar } from '@/components/ui/calendar';
import { Dispatch, SetStateAction, useEffect, useRef, useState } from 'react';
import { CalendarIcon } from 'lucide-react';
import { HttpTypes } from '@/types';
import { toast } from '@/components/ui/use-toast';

type PickupScheduleProps = {
  date?: Date;
  setDate?: Dispatch<SetStateAction<Date | undefined>>;
  selectedLocation?: HttpTypes.StoreOrderLocationPickup;
  preparationTime?: number; // in minutes
};

export default function PickupSchedule({
  date,
  setDate,
  selectedLocation,
  preparationTime,
}: PickupScheduleProps) {
  const selectedHourRef = useRef<HTMLButtonElement | null>(null);
  const scrollToHour = useRef(false);

  const [isOpen, setIsOpen] = useState(false);

  const operationMap = Object.fromEntries(
    selectedLocation?.location_operations.map((item) => [item.days, item]) || []
  );

  const hours = Array.from({ length: 24 }, (_, i) => i);

  /**
   * Handles the date selection in the calendar.
   * This function checks if the selected date is valid and falls within the operational hours of the
   * selected location. If valid, it sets the date state to the start of the operational hours for that date.
   * @param selectedDate - The date selected by the user.
   * @returns void
   * @description This function is triggered when a date is selected in the calendar component.
   */
  const handleDateSelect = (selectedDate: Date | undefined) => {
    if (!selectedDate) return;

    const timeRange = getDayTimeRange(selectedDate);
    if (!timeRange || timeRange.start >= timeRange.end) {
      toast({
        title: 'Invalid Date',
        description: 'The selected date is not within the operational hours.',
        variant: 'destructive',
      });
      setDate?.(undefined);
      return;
    }

    const now = new Date();
    const prepTimeMs = (preparationTime ?? 0) * 60 * 1000;

    const candidateTime = new Date(now.getTime() + prepTimeMs);
    const rawTime =
      selectedDate.toDateString() === now.toDateString()
        ? candidateTime < timeRange.start
          ? timeRange.start
          : candidateTime
        : timeRange.start;

    const rounded = roundUpToNextHour(rawTime);

    if (rounded > timeRange.end) {
      toast({
        title: 'No available time',
        description: 'All available hours today have passed.',
        variant: 'destructive',
      });
      setDate?.(undefined);
      return;
    }

    setDate?.(rounded);
    scrollToHour.current = true;
  };

  const roundUpToNextHour = (date: Date, stepMinutes: number = 15) => {
    const rounded = new Date(date.getTime());
    const ms = stepMinutes * 60 * 1000;
    rounded.setTime(Math.ceil(rounded.getTime() / ms) * ms);
    rounded.setSeconds(0, 0);
    return rounded;
  };

  const findFirstValidMinute = (
    baseDate: Date,
    timeRange: { start: Date; end: Date } | null,
    minValidTime: Date
  ) => {
    for (const m of [0, 15, 30, 45]) {
      const d = new Date(baseDate);
      d.setMinutes(m, 0, 0);
      if (timeRange && d >= timeRange.start && d <= timeRange.end && d >= minValidTime) {
        return m;
      }
    }
    return null;
  };

  /**
   * Handles the change of hour or minute in the time picker.
   * This function updates the date state with the new hour or minute value.
   * @param type - The type of time to change ('hour' or 'minute').
   * @param value - The new value for the hour or minute.
   * @returns void
   * @description This function is triggered when a user selects a new hour or minute in the time picker.
   */
  const handleTimeChange = (type: 'hour' | 'minute', value: string) => {
    if (!date) return;

    const newDate = new Date(date);

    if (type === 'hour') {
      newDate.setHours(parseInt(value));
    } else if (type === 'minute') {
      newDate.setMinutes(parseInt(value));
    }

    const timeRange = getDayTimeRange(newDate);
    const now = new Date();
    const prepTimeMs = (preparationTime ?? 0) * 60 * 1000;
    const minValidTime = new Date(now.getTime() + prepTimeMs);

    if (
      !timeRange ||
      newDate < timeRange.start ||
      newDate > timeRange.end ||
      newDate < minValidTime
    ) {
      if (type === 'hour') {
        const validMinute = findFirstValidMinute(newDate, timeRange, minValidTime);
        if (validMinute !== null) {
          newDate.setMinutes(validMinute);
        } else {
          toast({
            title: 'Invalid Time',
            description: 'The selected hour has no available minutes. Please choose another time.',
            variant: 'destructive',
          });
          return;
        }
      } else {
        toast({
          title: 'Invalid Time',
          description: 'The selected time is not available. Please choose another time.',
          variant: 'destructive',
        });
        return;
      }
    }

    setDate?.(newDate);
  };

  /**
   * Gets the start and end time range for the selected date based on the operational hours.
   * @param date - The date for which to get the time range.
   * @returns An object containing start and end Date objects, or null if the date is invalid.
   * @description This function checks the operational hours for the selected date and returns the adjusted
   * start and end times, considering preparation time.
   */
  const getDayTimeRange = (date?: Date) => {
    if (!date) return null;
    const day = date.toLocaleDateString('en-US', { weekday: 'long' });
    const dayConfig = operationMap[day];
    if (!dayConfig || !dayConfig.is_active) return null;

    const [startHour, startMinute] = dayConfig.start_time.split(':').map(Number);
    const [endHour, endMinute] = dayConfig.end_time.split(':').map(Number);

    const start = new Date(date);
    const end = new Date(date);

    start.setHours(startHour, startMinute, 0, 0);
    end.setHours(endHour, endMinute, 0, 0);

    return { start, end };
  };

  /**
   * Checks if a given date is disabled based on the current date, maximum days allowed, and operational hours.
   * @param date - The date to check.
   * @param maxDays - The maximum number of days allowed from today (default is 7).
   * @returns boolean - Returns true if the date is disabled, false otherwise.
   * @description This function checks if the selected date is before today, after the maximum allowed date,
   * or if it falls outside the operational hours of the selected location.
   */
  const handleDateDisabled = (date: Date, maxDays: number = 7) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const maxDate = new Date();
    maxDate.setHours(0, 0, 0, 0);
    maxDate.setDate(maxDate.getDate() + maxDays);

    const current = new Date(date);
    current.setHours(0, 0, 0, 0);

    const day = current.toLocaleDateString('en-US', { weekday: 'long' });
    const dayConfig = operationMap[day];

    const isBeforeToday = current < today;
    const isAfterLimit = current > maxDate;
    const isInactive = !dayConfig || !dayConfig.is_active;

    return isBeforeToday || isAfterLimit || isInactive;
  };

  useEffect(() => {
    if (!date) {
      setDate?.(undefined);
      return;
    }

    const parsedDate = new Date(date);

    if (!date || parsedDate.getTime() !== date.getTime()) {
      setDate?.(parsedDate);
    }
  }, [date]);

  useEffect(() => {
    if (scrollToHour.current && selectedHourRef.current) {
      requestAnimationFrame(() => {
        selectedHourRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'center',
        });
        scrollToHour.current = false;
      });
    }
  }, [date]);

  return (
    <Popover
      open={isOpen}
      onOpenChange={(open) => {
        selectedHourRef.current = null;
        scrollToHour.current = false;
        setIsOpen(open);
      }}
    >
      <PopoverTrigger asChild>
        <Button
          type='button'
          variant='outline'
          className={cn(
            'w-full justify-start text-left font-normal',
            !date && 'text-muted-foreground'
          )}
          disabled={!selectedLocation}
        >
          {date ? (
            format(date, 'EEEE, d MMMM HH:mm aa')
          ) : (
            <span className='text-left'>Select date and time</span>
          )}
          <CalendarIcon className='w-4 h-4 ml-auto opacity-50' />
        </Button>
      </PopoverTrigger>
      <PopoverContent className='w-auto p-0'>
        <div className='sm:flex'>
          <Calendar
            mode='single'
            selected={date}
            onSelect={handleDateSelect}
            initialFocus
            disabled={(date) => handleDateDisabled(date, 90)}
          />
          <div className='flex flex-col divide-y sm:h-[300px] sm:flex-row sm:divide-x sm:divide-y-0'>
            <ScrollArea className='w-64 sm:w-auto'>
              <div className='flex p-2 sm:flex-col'>
                {hours.map((hour) => {
                  const isSelected = date?.getHours() === hour;

                  const timeRange = getDayTimeRange(date ?? new Date());

                  const hasValidMinute = [0, 15, 30, 45].some((minute) => {
                    const testDate = new Date(date ?? new Date());
                    testDate.setHours(hour, minute, 0, 0);

                    const now = new Date();
                    const prepTimeMs = (preparationTime ?? 0) * 60 * 1000;
                    const minValidTime = new Date(now.getTime() + prepTimeMs);

                    return (
                      timeRange &&
                      testDate >= timeRange.start &&
                      testDate <= timeRange.end &&
                      testDate >= minValidTime
                    );
                  });

                  return (
                    <Button
                      key={hour}
                      type='button'
                      size='icon'
                      ref={isSelected ? selectedHourRef : undefined}
                      variant={isSelected ? 'default' : 'ghost'}
                      className='aspect-square shrink-0 sm:w-full'
                      onClick={() => handleTimeChange('hour', hour.toString())}
                      disabled={!hasValidMinute}
                    >
                      {hour.toString().padStart(2, '0')}
                    </Button>
                  );
                })}
              </div>
              <ScrollBar orientation='horizontal' className='sm:hidden' />
            </ScrollArea>
            <ScrollArea className='w-64 sm:w-auto'>
              <div className='flex p-2 sm:flex-col'>
                {[0, 15, 30, 45].map((minute) => {
                  const hour = date?.getHours() ?? 0;

                  const testDate = new Date(date ?? new Date());
                  testDate.setHours(hour, minute, 0, 0);

                  const now = new Date();
                  const prepTimeMs = (preparationTime ?? 0) * 60 * 1000;
                  const minValidTime = new Date(now.getTime() + prepTimeMs);

                  const timeRange = getDayTimeRange(testDate);

                  const isDisabled =
                    !timeRange ||
                    testDate < timeRange.start ||
                    testDate > timeRange.end ||
                    testDate < minValidTime;

                  const isSelected = date?.getMinutes() === minute;

                  return (
                    <Button
                      key={minute}
                      size='icon'
                      variant={isSelected ? 'default' : 'ghost'}
                      className='aspect-square shrink-0 sm:w-full'
                      onClick={() => handleTimeChange('minute', minute.toString())}
                      disabled={isDisabled}
                    >
                      {minute.toString().padStart(2, '0')}
                    </Button>
                  );
                })}
              </div>
              <ScrollBar orientation='horizontal' className='sm:hidden' />
            </ScrollArea>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
